// eslint-disable-next-line flowtype/require-valid-file-annotation
export interface ILogger {
  debug: (message: string, ...additionalDetails: any[]) => void;
  log: (messsage: string, ...additionalDetails: any[]) => void;
  info: (messsage: string, ...additionalDetails: any[]) => void;
  warn: (messsage: string, ...additionalDetails: any[]) => void;
  error: (messsage: string, ...additionalDetails: any[]) => void;
}

class Logger {
  constructor(
    onLog?: (
      level: string,
      message: string,
      data: Record<string, unknown>,
      fromConsoleWrap: boolean
    ) => void,
    logLevels?: Record<string, unknown>,
    shouldWrapConsole?: boolean
  ) {
    this.consoleLogLevels = {
      log: 'info',
      info: 'info',
      warn: 'warning',
      error: 'error',
    };

    this.onLog = onLog || this.onLogDefault;

    this.logLevels = logLevels || { ...this.consoleLogLevels };

    if (shouldWrapConsole) {
      this.wrapConsole();
    }

    this.setLogger();
  }

  getLogger(): ILogger {
    return this.logger;
  }

  onLogDefault(
    level: string,
    message: string,
    data?: Record<string, unknown>,
    isConsoleWrap?: boolean
  ) {
    if (isConsoleWrap) {
      return;
    }

    const isConsole = (key: string) => this.consoleLogLevels[key] === level;

    const consoleFuncName = Object.keys(this.consoleLogLevels).find(isConsole);

    // eslint-disable-next-line no-console
    const consoleFunc = consoleFuncName ? console[consoleFuncName] : console.log;

    consoleFunc(message, data);
  }

  setLogger() {
    this.logger = Object.keys(this.logLevels).reduce(
      (logger, logLevel) =>
        Object.assign(logger, {
          [logLevel]: (message: string, data: Record<string, unknown>) =>
            this.onLog(this.logLevels[logLevel], message, data, false),
        }),
      {}
    );
  }

  wrapConsole() {
    /* istanbul ignore else  */
    if (window && window.console) {
      // Store a ref to the original console object
      const actualConsole = window.console;

      // Set the console object as blank object, we'll add the functionality
      // back in
      window.console = {};

      // Add back all the props/values to the console object, but for the
      // functions, send the log to our server as well.
      Object.keys(actualConsole).forEach((m: string) => {
        // Only need to wrap the functions
        if (typeof actualConsole[m] === 'function') {
          // Set the wrapper, which pings our log server
          window.console[m] = (...args: any[]) => {
            // Only ping our log server for the functions we've mapped out a log
            // level for
            /* istanbul ignore else  */
            if (this.consoleLogLevels[m]) {
              this.onLog(
                this.consoleLogLevels[m],
                typeof args[0] === 'string' ? args[0] : m,
                args,
                true
              );
            }

            // Perform the original console function, so it appears in the
            // console
            actualConsole[m](...args);
          };
        } else {
          // Not concerned with this, so put it back as usual
          window.console[m] = actualConsole[m];
        }
      });
    }
  }
}

export default Logger;
